import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Search, Check, Ban, ScrollText, Shield, RefreshCw, Users } from "lucide-react";
import { SectionTitle, Badge } from "@/components/Bits";
import { fmtTime } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_FILTERS = [
  { v: "all", label: "All" },
  { v: "pending", label: "Pending" },
  { v: "approved", label: "Approved" },
  { v: "disabled", label: "Disabled" },
  { v: "admin", label: "Admins" },
];

const ACTION_LABEL = {
  approve: "APPROVED",
  revoke: "REVOKED",
  disable: "DISABLED",
  enable: "ENABLED",
  role_change: "ROLE",
  create: "CREATED",
  delete: "DELETED",
};

const ACTION_KIND = {
  approve: "on", revoke: "warn", disable: "warn", enable: "on",
  role_change: "neutral", create: "on", delete: "warn",
};

export default function AdminPanel() {
  const { profile } = useAuth();
  const [tab, setTab] = useState("users");

  // Users tab state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // System tab state
  const [liveStatus, setLiveStatus] = useState(null);
  const [authStatus, setAuthStatus] = useState(null);
  const [sysLoading, setSysLoading] = useState(false);
  const [sysError, setSysError] = useState(null);

  // Audit tab state
  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditAction, setAuditAction] = useState("all");

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const r = await axios.get(`${API}/admin/users`);
      setUsers(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadSystem = async () => {
    setSysLoading(true);
    setSysError(null);
    try {
      const [live, status] = await Promise.all([
        axios.get(`${API}/debug/live-status`).then((r) => r.data).catch(() => null),
        axios.get(`${API}/auth/status`).then((r) => r.data).catch(() => null),
      ]);
      setLiveStatus(live);
      setAuthStatus(status);
    } catch (e) {
      setSysError(e?.response?.data?.detail || e.message);
    } finally {
      setSysLoading(false);
    }
  };

  const loadAudit = async () => {
    setAuditLoading(true);
    try {
      const params = auditAction !== "all" ? `?action=${auditAction}` : "";
      const r = await axios.get(`${API}/admin/audit-log${params}`);
      setAudit(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "users") loadUsers();
    if (tab === "system") loadSystem();
    if (tab === "audit") loadAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, auditAction]);

  // --- User-mgmt actions ----------------------------------------------------
  const setRole = async (id, role) => {
    try {
      await axios.put(`${API}/admin/users/${id}/role`, { role });
      toast.success(`Role updated to ${role}`);
      loadUsers();
    } catch (ex) { toast.error(ex?.response?.data?.detail || ex.message); }
  };

  const toggle = async (u) => {
    const ok = window.confirm(
      u.disabled ? `Re-enable ${u.email}?` : `Disable ${u.email}? They will lose access immediately.`,
    );
    if (!ok) return;
    try {
      await axios.post(`${API}/admin/users/${u.id}/${u.disabled ? "enable" : "disable"}`);
      toast.success(u.disabled ? "User enabled" : "User disabled");
      loadUsers();
    } catch (ex) { toast.error(ex?.response?.data?.detail || ex.message); }
  };

  const setApproval = async (u, approve) => {
    try {
      await axios.post(`${API}/admin/users/${u.id}/${approve ? "approve" : "revoke"}`);
      toast.success(approve ? `Approved ${u.email}` : `Revoked ${u.email}`);
      loadUsers();
    } catch (ex) { toast.error(ex?.response?.data?.detail || ex.message); }
  };

  const remove = async (u) => {
    if (u.email === profile?.email) {
      toast.error("Cannot delete the currently signed-in admin.");
      return;
    }
    if (!window.confirm(`Permanently delete ${u.email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}`);
      toast.success("User deleted");
      loadUsers();
    } catch (ex) { toast.error(ex?.response?.data?.detail || ex.message); }
  };

  // --- Derived ---------------------------------------------------------------
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (q && !(u.email || "").toLowerCase().includes(q)) return false;
      switch (statusFilter) {
        case "pending":  return !u.approved && !u.disabled;
        case "approved": return u.approved && !u.disabled;
        case "disabled": return u.disabled;
        case "admin":    return u.role === "admin";
        default: return true;
      }
    });
  }, [users, search, statusFilter]);

  const counts = useMemo(() => ({
    total: users.length,
    pending: users.filter((u) => !u.approved && !u.disabled).length,
    approved: users.filter((u) => u.approved && !u.disabled).length,
    disabled: users.filter((u) => u.disabled).length,
    admin: users.filter((u) => u.role === "admin").length,
  }), [users]);

  const liveRows = useMemo(() => {
    if (!liveStatus) return [];
    const pick = (k) => (k in liveStatus ? liveStatus[k] : null);
    return [
      ["LIVE_TRADING_ENABLED (raw)", String(pick("LIVE_TRADING_ENABLED_raw") ?? "unset")],
      ["LIVE_TRADING_ENABLED (parsed)", String(pick("LIVE_TRADING_ENABLED_parsed") ?? "false")],
      ["USE_STRATEGY_PROFILE_ENGINE", String(pick("USE_STRATEGY_PROFILE_ENGINE_parsed") ?? "—")],
      ["BINANCE_LIVE_FAPI_BASE", String(pick("BINANCE_LIVE_FAPI_BASE_resolved") ?? "—")],
      ["USER_BINANCE_CREDENTIALS_CONFIGURED", String(pick("USER_BINANCE_CREDENTIALS_CONFIGURED") ?? "—")],
      ["ENCRYPTION_KEY_PRESENT", String(pick("ENCRYPTION_KEY_PRESENT") ?? "—")],
    ];
  }, [liveStatus]);

  return (
    <div className="p-6 space-y-6">
      <SectionTitle sub="Approve users, manage roles, review audit log and runtime visibility.">
        Admin Panel
      </SectionTitle>

      {/* Tab bar */}
      <div className="flex border-b border-border">
        {[
          { k: "users", label: "Users", count: counts.total, icon: <Users size={12} /> },
          { k: "system", label: "System", count: null, icon: <Shield size={12} /> },
          { k: "audit", label: "Audit Log", count: null, icon: <ScrollText size={12} /> },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`admin-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2.5 text-xs uppercase tracking-widest transition-colors inline-flex items-center gap-2 ${
              tab === t.k
                ? "text-white border-b-2 border-[#00E676] -mb-px"
                : "text-[#71717A] hover:text-[#A0A0AB]"
            }`}
          >
            {t.icon}
            {t.label}
            {t.count != null ? (
              <span className="font-mono normal-case tracking-normal text-[10px] text-[#71717A]">({t.count})</span>
            ) : null}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-stats">
            {[
              { label: "Total", value: counts.total, color: "white" },
              { label: "Pending", value: counts.pending, color: "#F59E0B" },
              { label: "Approved", value: counts.approved, color: "#00E676" },
              { label: "Disabled", value: counts.disabled, color: "#FF3B30" },
              { label: "Admins", value: counts.admin, color: "#A78BFA" },
            ].map((s) => (
              <div key={s.label} className="bg-[#0F0F14] border border-border rounded-sm px-4 py-3">
                <div className="text-[10px] uppercase tracking-widest text-[#71717A]">{s.label}</div>
                <div className="text-2xl font-mono mt-1" style={{ color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center" data-testid="admin-filters">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
              <Input
                data-testid="admin-search"
                placeholder="Search by email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-[#1A1A22] border-border rounded-sm font-mono pl-9"
              />
            </div>
            <div className="flex gap-1 flex-wrap">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.v}
                  data-testid={`admin-filter-${f.v}`}
                  onClick={() => setStatusFilter(f.v)}
                  className={`px-3 py-1.5 text-xs uppercase tracking-widest rounded-sm border transition-colors ${
                    statusFilter === f.v
                      ? "border-[#00E676] bg-[#00E676]/10 text-[#00E676]"
                      : "border-border bg-[#1A1A22] text-[#A0A0AB] hover:text-white"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <Button
              data-testid="admin-refresh-users"
              onClick={loadUsers}
              variant="outline"
              className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633] text-xs"
            >
              <RefreshCw size={14} className={`mr-1 ${usersLoading ? "animate-spin" : ""}`} strokeWidth={1.5} />
              Refresh
            </Button>
          </div>

          {/* Table */}
          <div className="bg-[#0F0F14] border border-border rounded-sm overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead className="text-[10px] uppercase tracking-widest text-[#71717A]">
                <tr>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Role</th>
                  <th className="text-left px-4 py-3">Approval</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Approved at</th>
                  <th className="text-left px-4 py-3">Last sign-in</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {usersLoading ? (
                  <tr><td colSpan={7} className="px-4 py-6 text-center text-[#71717A]">Loading…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-6 text-center text-[#71717A]">
                    {users.length === 0 ? "No users yet." : "No users match filters."}
                  </td></tr>
                ) : filtered.map((u) => (
                  <tr key={u.id} className="border-t border-border/60" data-testid={`admin-row-${u.email}`}>
                    <td className="px-4 py-2 font-mono">{u.email}</td>
                    <td className="px-4 py-2">
                      <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
                        <SelectTrigger className="bg-[#1A1A22] border-border rounded-sm h-8 w-28"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">user</SelectItem>
                          <SelectItem value="admin">admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-2">
                      <Badge kind={u.approved ? "on" : "warn"}>
                        {u.approved ? "APPROVED" : "PENDING"}
                      </Badge>
                    </td>
                    <td className="px-4 py-2">
                      <Badge kind={u.disabled ? "warn" : "on"}>{u.disabled ? "DISABLED" : "ACTIVE"}</Badge>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-[#A0A0AB]">{fmtTime(u.approved_at)}</td>
                    <td className="px-4 py-2 font-mono text-xs text-[#A0A0AB]">{fmtTime(u.last_sign_in_at)}</td>
                    <td className="px-4 py-2 text-right space-x-1.5 whitespace-nowrap">
                      {!u.approved ? (
                        <Button data-testid={`admin-approve-${u.email}`} size="sm" onClick={() => setApproval(u, true)} className="rounded-sm bg-[#00E676] text-black hover:bg-[#00C966]">
                          <Check size={14} strokeWidth={2} className="mr-1" /> Approve
                        </Button>
                      ) : (
                        <Button data-testid={`admin-revoke-${u.email}`} size="sm" variant="outline" onClick={() => setApproval(u, false)} className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633]">
                          <Ban size={14} strokeWidth={1.5} className="mr-1" /> Revoke
                        </Button>
                      )}
                      <Button data-testid={`admin-toggle-${u.email}`} size="sm" variant="outline" onClick={() => toggle(u)} className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633]">
                        {u.disabled ? "Enable" : "Disable"}
                      </Button>
                      <Button data-testid={`admin-delete-${u.email}`} size="sm" variant="outline" onClick={() => remove(u)} className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633]" disabled={u.email === profile?.email}>
                        <Trash2 size={14} strokeWidth={1.5} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "system" && (
        <div className="space-y-6" data-testid="admin-system-tab">
          <div className="bg-[#0F0F14] border border-border rounded-sm p-5">
            <div className="flex items-center gap-2 mb-3">
              <Shield size={14} className="text-[#00E676]" strokeWidth={1.5} />
              <div className="text-xs uppercase tracking-widest text-white">Current Operator</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="admin-current-operator">
              <Row label="Email" value={profile?.email || "—"} mono />
              <Row label="User ID" value={profile?.id || "—"} mono />
              <Row label="Role" value={<Badge kind={profile?.role === "admin" ? "on" : "neutral"}>{(profile?.role || "user").toUpperCase()}</Badge>} />
            </div>
          </div>
          <div className="bg-[#0F0F14] border border-border rounded-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-white">Runtime</div>
              <button data-testid="admin-runtime-refresh" onClick={loadSystem} disabled={sysLoading} className="inline-flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-widest rounded-sm border border-border bg-[#1A1A22] text-[#A0A0AB] hover:text-white">
                <RefreshCw size={12} strokeWidth={1.5} className={sysLoading ? "animate-spin" : ""} />
                Refresh
              </button>
            </div>
            {sysError ? (
              <div className="text-xs text-[#FF7A6E]">{sysError}</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-runtime-grid">
                <Row label="Auth required" value={String(authStatus?.auth_required ?? "—")} mono />
                <Row label="Auth backend" value={String(authStatus?.auth_backend ?? "—")} mono />
                <Row label="Signup disabled" value={String(authStatus?.signup_disabled ?? "—")} mono />
                <Row label="Supabase project ref" value={String(authStatus?.supabase_project_ref ?? "—")} mono />
                {liveRows.map(([k, v]) => (<Row key={k} label={k} value={v} mono />))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "audit" && (
        <>
          <div className="flex items-center gap-2 flex-wrap" data-testid="audit-filters">
            <span className="text-[10px] uppercase tracking-widest text-[#71717A] inline-flex items-center gap-1.5">
              <ScrollText size={12} strokeWidth={1.5} /> Filter:
            </span>
            {["all", ...Object.keys(ACTION_LABEL)].map((a) => (
              <button
                key={a}
                data-testid={`audit-filter-${a}`}
                onClick={() => setAuditAction(a)}
                className={`px-2.5 py-1 text-[10px] uppercase tracking-widest rounded-sm border ${
                  auditAction === a
                    ? "border-[#00E676] bg-[#00E676]/10 text-[#00E676]"
                    : "border-border bg-[#1A1A22] text-[#A0A0AB] hover:text-white"
                }`}
              >
                {a === "all" ? "All" : ACTION_LABEL[a] || a}
              </button>
            ))}
          </div>
          <div className="bg-[#0F0F14] border border-border rounded-sm overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead className="text-[10px] uppercase tracking-widest text-[#71717A]">
                <tr>
                  <th className="text-left px-4 py-3">When</th>
                  <th className="text-left px-4 py-3">Action</th>
                  <th className="text-left px-4 py-3">Actor</th>
                  <th className="text-left px-4 py-3">Target</th>
                  <th className="text-left px-4 py-3">Metadata</th>
                </tr>
              </thead>
              <tbody>
                {auditLoading ? (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-[#71717A]">Loading…</td></tr>
                ) : audit.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-[#71717A]">No audit entries yet.</td></tr>
                ) : audit.map((e) => (
                  <tr key={e.id} className="border-t border-border/60">
                    <td className="px-4 py-2 font-mono text-xs text-[#A0A0AB]">{fmtTime(e.timestamp)}</td>
                    <td className="px-4 py-2"><Badge kind={ACTION_KIND[e.action] || "neutral"}>{ACTION_LABEL[e.action] || e.action.toUpperCase()}</Badge></td>
                    <td className="px-4 py-2 font-mono text-xs">{e.actor_email || e.actor_id || "—"}</td>
                    <td className="px-4 py-2 font-mono text-xs">{e.target_email || e.target_user_id || "—"}</td>
                    <td className="px-4 py-2 font-mono text-[10px] text-[#71717A]">{e.metadata && Object.keys(e.metadata).length ? JSON.stringify(e.metadata) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Row({ label, value, mono = false }) {
  return (
    <div className="bg-[#1A1A22] border border-border rounded-sm px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-widest text-[#71717A]">{label}</div>
      <div className={`mt-1 text-sm text-white ${mono ? "font-mono break-all" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}
